import React, { ChangeEvent, useState, useMemo, useEffect } from "react";
import {
  FormTab,
  TextInput,
  SelectInput,
  BooleanInput,
  maxLength,
  TabbedForm,
} from "react-admin";
import { required, FormDataConsumer, useTranslate } from "react-admin";
import "../../common/styles/form-style.css";
import { useAppData } from "../../../common/hooks/useAppData";
import { DocumentUploader } from "../../common/form/DocumentUploader";
import { MakeModelSelector } from "./MakeModelSelector";
import { YearColorSelector } from "./YearColorSelector";
import { LicenceStateSelector } from "../../common/form/LicenceStateSelector";
import { useDefaultHosData } from "../../../common/hooks/useDefaultHosData";
import { ICompanyDefaultHosData } from "../../../common/redux/actions/defaultHosData";
import { VehicleDefaultValuesHandler } from "./VehicleDefaultValuesHandler";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";
import { FormToolbar } from "../../common/form/FormToolbar";
import { onlyNumbersAndCapitalLetters } from "./validations";

export const VehicleForm = (props: any) => {
  const defaultHosDataState = useDefaultHosData();
  const [currentDefaultHosData, setCurrentDefaultHosData] = useState<
    Omit<ICompanyDefaultHosData, "companyId">
  >();

  const translate = useTranslate();
  const countries = useAppData((x) => x.countries);
  const companies = useAppData((x) => x.companies);
  const documentTypes =
    useAppData(
      (x) => x.knownDocumentTypes && x.knownDocumentTypes["Vehicle"]
    ) ?? [];

  const fuelTypeValues = useAppData((x) => x.fuelTypes) ?? [];
  const fuelTypes = fuelTypeValues.map((x) => ({
    id: x,
    name: translate(`appData.fuelTypes.${x}`),
  }));
  const odometerTypeValues = useAppData((x) => x.odometerTypes) ?? [];
  const odometerTypes = odometerTypeValues.map((x) => ({
    id: x,
    name: translate(`appData.odometerTypes.${x}`),
  }));
  const eldDevices = useAppData((x) => x.eldDevices) ?? [];
  let unassignedElds = eldDevices
    .filter((x) => !x.assigned)
    .map((x) => ({ id: x.id, name: x.serialNumber }));

  if (props.record && props.record.eldDeviceId) {
    unassignedElds = [
      ...unassignedElds,
      {
        id: props.record.eldDeviceId,
        name: eldDevices.find((x) => x.id === props.record.eldDeviceId)
          ?.serialNumber,
      },
    ];
  }

  const numYears = new Date().getFullYear() - 2000;
  const years = [...Array(numYears + 2)].map((_, i) => ({
    id: 2000 + i,
    name: (2000 + i).toString(),
  }));

  const defaultCompany = useMemo(
    () => (companies.length === 1 ? companies[0] : undefined),
    [companies]
  );
  useEffect(() => {
    if (!defaultCompany) {
      return;
    }

    setCurrentDefaultHosData(defaultHosDataState[defaultCompany.id!]);
  }, [defaultCompany, defaultHosDataState]);

  return (
    <TabbedForm
      className="form-create-edit"
      variant="outlined"
      {...props}
      toolbar={<FormToolbar />}
    >
      <FormTab label="resources.admin-vehicles.labels.generalInformation">
        <MakeModelSelector {...props} />
        <YearColorSelector years={years} />
        <FormDataConsumer>
          {(formDataProps) => (
            <LicenceStateSelector
              countryIdProp="countryId"
              stateIdProp="stateId"
              countries={countries}
              {...formDataProps}
            />
          )}
        </FormDataConsumer>
        <ProtectedElement source="hasSleeperBerth">
          <BooleanInput
            source="hasSleeperBerth"
            isRequired
            label="Has Sleeper Berth"
            defaultValue={props.isEdit ? undefined : true}
          />
        </ProtectedElement>
        <ProtectedElement source="licensePlate">
          <TextInput
            source="licensePlate"
            label="resources.admin-vehicles.labels.licencePlateNumber"
          />
        </ProtectedElement>
        <ProtectedElement source="odometer">
          <SelectInput
            label="resources.admin-vehicles.labels.odometerType"
            source="odometer"
            choices={odometerTypes}
            validate={required()}
          />
        </ProtectedElement>
        <ProtectedElement source="odometerOffset">
          <TextInput
            source="odometerOffset"
            label="resources.admin-vehicles.labels.odometerOffset"
            type="number"
            validate={required()}
            defaultValue={props.isEdit ? undefined : 0}
          />
        </ProtectedElement>
        <ProtectedElement source="fuelType">
          <SelectInput
            label="resources.admin-vehicles.labels.fuelType"
            source="fuelType"
            choices={fuelTypes}
          />
        </ProtectedElement>
      </FormTab>
      <FormTab label="resources.admin-vehicles.labels.companyInformation">
        <ProtectedElement source="name">
          <TextInput
            source="name"
            label="resources.admin-vehicles.labels.vehicleId"
            validate={[
              required(),
              maxLength(10),
              onlyNumbersAndCapitalLetters("messages.Vehicle.InvalidVehicleId"),
            ]}
          />
        </ProtectedElement>
        <ProtectedElement source="companyId">
          <SelectInput
            source="companyId"
            label="resources.admin-vehicles.labels.company"
            choices={companies}
            validate={required()}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const currentDefault =
                defaultHosDataState[parseInt(event.target.value)];
              setCurrentDefaultHosData(currentDefault);
            }}
            defaultValue={defaultCompany?.id}
          />
        </ProtectedElement>
        <ProtectedElement source="companyOwned">
          <BooleanInput
            label="resources.admin-vehicles.labels.companyOwned"
            source="companyOwned"
          />
        </ProtectedElement>
        <ProtectedElement source="eldDeviceId">
          <SelectInput
            source="eldDeviceId"
            choices={unassignedElds}
            label="resources.admin-vehicles.labels.eldSerialNumber"
            allowEmpty
            emptyText="resources.admin-vehicles.labels.noEldDevice"
          />
        </ProtectedElement>
      </FormTab>
      <FormTab label="resources.common.labels.documents">
        <ProtectedElement source="documents">
          <DocumentUploader source="documents" documentTypes={documentTypes} />
        </ProtectedElement>
      </FormTab>
      <VehicleDefaultValuesHandler {...currentDefaultHosData} />
    </TabbedForm>
  );
};
